import { AlloyLegacyThemeDesignTokens, DatePickerDesignTokens } from '../types';
import { buildBoxShadow } from './common';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

const parsePx = (value?: string): number | undefined => {
  if (value === undefined) {
    return undefined;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const buildInsetBoxShadow = (
  x?: string,
  y?: string,
  blur?: string,
  spread?: string,
  color?: string,
) =>
  x !== undefined &&
  y !== undefined &&
  blur !== undefined &&
  spread !== undefined &&
  color !== undefined
    ? `inset ${x} ${y} ${blur} ${spread} ${color}`
    : undefined;

const joinBoxShadows = (
  ...shadows: (string | undefined)[]
): string | undefined => {
  const valid = shadows.filter(
    (shadow): shadow is string => shadow !== undefined,
  );

  return valid.length > 0 ? valid.join(', ') : undefined;
};

type WeaveRangeCellTokens = {
  cell?: {
    'text-color'?: { selected?: string };
    'background-color'?: { selected?: string };
  };
  range?: {
    cell?: {
      'background-color'?: { default?: string; hover?: string };
    };
  };
};

const getWeaveRangeMiddleCell = (
  dp: WeaveRangeCellTokens | undefined,
  textPrimary?: string,
) => ({
  'border-radius': '0',
  color: textPrimary,
  'background-color': dp?.range?.cell?.['background-color']?.default,
  '&:hover': {
    color: dp?.cell?.['text-color']?.selected,
    'background-color': dp?.range?.cell?.['background-color']?.hover,
  },
});

const getWeaveRangeEndpointCell = (dp: WeaveRangeCellTokens | undefined) => ({
  color: dp?.cell?.['text-color']?.selected,
  'border-radius': '0',
  'background-color': dp?.cell?.['background-color']?.selected,
  '&:hover': {
    'background-color': dp?.cell?.['background-color']?.selected,
  },
});

export const getDatePickerTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
  transitionDuration: number,
  transitionEasing: string,
): DatePickerDesignTokens => ({
  field: {
    _: {
      display: 'flex',
      height: '36px',
      width: '300px',
      'padding-left': `${theme.spacing.S}px`,
      'padding-right': `${theme.spacing.S}px`,
      'align-items': 'center',
      outline: 'none',
      'background-color': theme.tokens.colors.background.primary.value,
      'border-width': '1px',
      'border-style': 'solid',
      'border-color': theme.tokens.colors.border.value,
      'border-radius': `${theme.borderRadius.small}px`,
      cursor: 'pointer',
      transition: `box-shadow ${transitionDuration}ms ${transitionEasing}, border-color ${transitionDuration}ms ${transitionEasing}`,
      '&:hover': {
        'box-shadow': theme.halos.hover,
      },
      '&:focus': {
        'box-shadow': theme.halos.focus,
        'border-color': theme.tokens.colors.border.focused.value,
      },
      '&:active': {
        'box-shadow': theme.halos.active,
        'border-color': theme.tokens.colors.border.value,
      },
      '& svg': {
        color: theme.tokens.colors.icon.secondary.value,
      },
    },
    error: {
      true: {
        'border-color': theme.tokens.colors.border.error.value,
      },
      false: {},
    },
    disabled: {
      true: {
        cursor: 'default',
        'background-color':
          theme.tokens.colors.background.neutral.inactive.value,
        'border-color': theme.tokens.colors.border.inactive.value,
        '&:hover': {
          'border-color': theme.tokens.colors.border.inactive.value,
          'box-shadow': 'none',
        },
        '&:focus': {
          'border-color': theme.tokens.colors.border.inactive.value,
          'box-shadow': 'none',
        },
        '&:active': {
          'border-color': theme.tokens.colors.border.inactive.value,
          'box-shadow': 'none',
        },
        '& svg': {
          color: theme.tokens.colors.icon.inactive.value,
        },
      },
      false: {},
    },
  },
  fieldValue: {
    _: {
      display: 'flex',
      'padding-top': `${theme.spacing.XXXS}px`,
      'padding-bottom': `${theme.spacing.XXXS}px`,
      'padding-left': `${theme.spacing.XXS}px`,
      'padding-right': `${theme.spacing.XXS}px`,
      'margin-left': '10px',
      'margin-right': '10px',
      'align-items': 'center',
      outline: 'none',
      'border-radius': `${theme.borderRadius.medium}px`,
      ...theme.typography.labelMedium,
    },
    selecting: {
      true: {
        'background-color': theme.tokens.colors.background.focused.value,
      },
      false: {
        'background-color': 'transparent',
      },
    },
    hasValue: {
      true: {
        color: theme.tokens.colors.text.primary.value,
      },
      false: {
        color: theme.tokens.colors.text.inactive.value,
      },
    },
  },
  cell: {
    _: {
      ...theme.typography.bodyMediumBold,
      'box-sizing': 'border-box',
      'border-right-width': '0',
      'border-right-style': 'solid',
      'border-right-color': 'transparent',
      'border-bottom-width': '0',
      'border-bottom-style': 'solid',
      'border-bottom-color': 'transparent',
    },
    variant: {
      default: {
        color: theme.tokens.colors.text.primary.value,
        'border-radius': `${theme.borderRadius.medium}px`,
        '&:hover': {
          'background-color':
            theme.tokens.colors.background.neutral.hover.value,
        },
        '&:focus': {
          'background-color': theme.tokens.colors.background.primary.value,
          'box-shadow': `inset 0px 0px 0px 1px ${theme.tokens.colors.border.focused.value}, inset ${theme.halos.focus}`,
        },
        '&:active': {
          'background-color':
            theme.tokens.colors.background.neutral.pressed.value,
        },
      },
      rangeMiddle: {
        'border-radius': `${theme.borderRadius.none}`,
        'background-color':
          theme.tokens.colors.background.selected.subtler.value,
        '&:hover': {
          color: theme.tokens.colors.text.inverse.value,
          'background-color': theme.tokens.colors.background.hover.value,
        },
      },
      rangeStart: {
        color: theme.tokens.colors.text.inverse.value,
        'border-top-left-radius': `${theme.borderRadius.medium}px`,
        'border-bottom-left-radius': `${theme.borderRadius.medium}px`,
        'border-top-right-radius': `${theme.borderRadius.none}`,
        'border-bottom-right-radius': `${theme.borderRadius.none}`,
        'background-color': theme.tokens.colors.background.selected.value,
        '&:hover': {
          color: theme.tokens.colors.text.inverse.value,
          'background-color': theme.tokens.colors.background.hover.value,
        },
      },
      rangeEnd: {
        color: theme.tokens.colors.text.inverse.value,
        'border-top-left-radius': `${theme.borderRadius.none}`,
        'border-bottom-left-radius': `${theme.borderRadius.none}`,
        'border-top-right-radius': `${theme.borderRadius.medium}px`,
        'border-bottom-right-radius': `${theme.borderRadius.medium}px`,
        'background-color': theme.tokens.colors.background.selected.value,
        '&:hover': {
          color: theme.tokens.colors.text.inverse.value,
          'background-color': theme.tokens.colors.background.hover.value,
        },
      },
      outsideRangeMiddle: {
        'border-radius': '0',
        'background-color':
          theme.tokens.colors.background.selected.subtler.value,
        '&:hover': {
          color: theme.tokens.colors.text.inverse.value,
          'background-color': theme.tokens.colors.background.hover.value,
        },
      },
      outsideRangeStart: {
        color: theme.tokens.colors.text.inverse.value,
        'border-top-left-radius': `${theme.borderRadius.medium}px`,
        'border-bottom-left-radius': `${theme.borderRadius.medium}px`,
        'border-top-right-radius': `${theme.borderRadius.none}`,
        'border-bottom-right-radius': `${theme.borderRadius.none}`,
        'background-color': theme.tokens.colors.background.selected.value,
        '&:hover': {
          color: theme.tokens.colors.text.inverse.value,
          'background-color': theme.tokens.colors.background.hover.value,
        },
      },
      outsideRangeEnd: {
        color: theme.tokens.colors.text.inverse.value,
        'border-top-left-radius': `${theme.borderRadius.none}`,
        'border-bottom-left-radius': `${theme.borderRadius.none}`,
        'border-top-right-radius': `${theme.borderRadius.medium}px`,
        'border-bottom-right-radius': `${theme.borderRadius.medium}px`,
        'background-color': theme.tokens.colors.background.selected.value,
        '&:hover': {
          color: theme.tokens.colors.text.inverse.value,
          'background-color': theme.tokens.colors.background.hover.value,
        },
      },
      outside: {
        'border-radius': `${theme.borderRadius.medium}px`,
        color: theme.tokens.colors.text.inactive.value,
        'background-color': theme.tokens.colors.background.primary.value,
        '&:hover, &:focus, &:active': {
          'background-color': theme.tokens.colors.background.secondary.value,
        },
      },
      spacer: {
        cursor: 'default',
        'pointer-events': 'none',
        ...theme.typography.bodyMedium,
      },
    },
    today: {
      true: {
        color: theme.tokens.colors.text.selected.value,
      },
      false: {},
    },
    disabled: {
      true: {
        color: theme.tokens.colors.text.inactive.value,
        'background-color': theme.tokens.colors.background.primary.value,
        cursor: 'not-allowed',
        '&:hover': {
          'box-shadow': 'none',
          'background-color': theme.tokens.colors.background.primary.value,
        },
        '&:focus, &:active': {
          'background-color': theme.tokens.colors.background.primary.value,
          'box-shadow': 'none',
        },
      },
      false: {},
    },
    selected: {
      true: {
        color: theme.tokens.colors.text.inverse.value,
        'background-color': theme.tokens.colors.background.selected.value,
        '&:hover, &:focus, &:active': {
          'box-shadow': 'none',
          'background-color': theme.tokens.colors.background.selected.value,
        },
      },
      false: {},
    },
    compoundVariants: [
      {
        variant: 'default',
        today: 'true',
        styles: {
          '&:active': {
            'box-shadow': `inset 0px 0px 0px 1px ${theme.tokens.colors.border.selected.value}`,
          },
        },
      },
    ],
  },
  eventDot: {
    _: {
      position: 'absolute',
      top: '30px',
      'border-radius': theme.borderRadius.circle,
      'min-height': '6px',
      'min-width': '6px',
      height: '6px',
      width: '6px',
      'max-height': '6px',
      'max-width': '6px',
    },
    selected: {
      true: {
        'background-color': theme.tokens.colors.text.inverse.value,
      },
      false: {
        'background-color': theme.tokens.colors.text.primary.value,
      },
    },
  },
  calendar: {
    _: {
      display: 'flex',
      'flex-direction': 'column',
      'padding-left': `${theme.spacing.M}px`,
      'padding-right': `${theme.spacing.M}px`,
    },
    header: {
      display: 'flex',
      'justify-content': 'center',
      'align-items': 'center',
      'margin-bottom': `${theme.spacing.S}px`,
      height: '40px',
      position: 'relative',
    },
    navButton: {
      position: 'absolute',
      color: theme.tokens.colors.icon.primary.value,
      '&:disabled': {
        color: theme.tokens.colors.icon.inactive.value,
      },
    },
    grid: {
      'border-top-width': '0',
      'border-top-style': 'solid',
      'border-top-color': 'transparent',
      'border-left-width': '0',
      'border-left-style': 'solid',
      'border-left-color': 'transparent',
    },
  },
  overlay: {
    _: {
      'min-height': '321px',
      display: 'flex',
      'flex-direction': 'column',
      'background-color': theme.tokens.colors.background.primary.value,
      'box-shadow': theme.shadows.highElevation,
      'border-radius': '6px',
      'padding-top': `${theme.spacing.M}px`,
      'padding-bottom': `${theme.spacing.M}px`,
    },
  },
  yearMonthDropdown: {
    _: {
      color: theme.tokens.colors.text.primary.value,
      border: 'none',
      width: 'auto',
      'padding-left': `${theme.spacing.XS}px`,
      'padding-right': `${theme.spacing.XS}px`,
      'margin-left': `${theme.spacing.M}px`,
    },
    disabled: {
      true: {
        color: theme.tokens.colors.text.inactive.value,
      },
      false: {},
    },
    icon: {
      'margin-left': `${theme.spacing.XS}px`,
      width: '18px',
      height: '18px',
    },
  },
  toggleIcon: {
    _: {
      color: theme.tokens.colors.icon.secondary.value,
    },
    open: {
      true: {
        transform: 'rotate(180deg)',
      },
      false: {},
    },
  },
  footer: {
    divider: {
      margin: '15px 0',
    },
    _: {
      'padding-top': '0px',
      'padding-bottom': '0px',
      'padding-left': `${theme.spacing.M}px`,
      'padding-right': `${theme.spacing.M}px`,
    },
  },
  arrow: {
    'background-color': theme.tokens.colors.background.primary.value,
  },
  cellWidth: 40,
  cellHeight: 40,
  calendarWidth: 312,
});

export const getDatePickerTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): DatePickerDesignTokens => {
  const dp = tokens?.component?.datepicker;
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;
  const input = tokens?.component?.input;
  const weaveButton = tokens?.component?.button;

  const cellSeparatorColor = dp?.cell?.['border-color']?.default;

  const focusBoxShadow = buildInsetBoxShadow(
    dp?.cell?.['box-shadow-x']?.focus,
    dp?.cell?.['box-shadow-y']?.focus,
    dp?.cell?.['box-shadow-blur']?.focus,
    dp?.cell?.['box-shadow-spread']?.focus,
    dp?.cell?.['box-shadow-color']?.focus,
  );

  const borderRadius4 = generic?.['border-radius']?.['4'];

  const textPrimary = semantic?.['text-color']?.medium?.default;
  const textSubtle = semantic?.['text-color']?.light;
  const iconMedium = semantic?.['icon-color']?.medium;
  const bgSurface = semantic?.['background-color']?.surface?.[100];

  const todayInsetRing =
    dp?.cell?.['border-width']?.current !== undefined &&
    dp?.cell?.['border-color']?.current !== undefined
      ? `inset 0 0 0 ${dp.cell['border-width'].current} ${dp.cell['border-color'].current}`
      : undefined;

  const selectedFocusInset = buildInsetBoxShadow(
    dp?.cell?.['box-shadow-x']?.selected?.focus,
    dp?.cell?.['box-shadow-y']?.selected?.focus,
    dp?.cell?.['box-shadow-blur']?.selected?.focus,
    dp?.cell?.['box-shadow-spread']?.selected?.focus,
    dp?.cell?.['box-shadow-color']?.selected?.focus,
  );

  const cellWidth = parsePx(dp?.cell?.day?.width);
  const cellHeight = parsePx(dp?.cell?.height);
  const calendarWidth = parsePx(dp?.['calendar-container']?.width);
  const doubleCalendarWidth = parsePx(dp?.range?.['calendar-container']?.width);

  const fieldTransition =
    input?.['transition-duration'] !== undefined &&
    input?.['transition-timing-function'] !== undefined
      ? `box-shadow ${input['transition-duration']} ${input['transition-timing-function']}, border-color ${input['transition-duration']} ${input['transition-timing-function']}`
      : undefined;

  const cellFontSize = dp?.cell?.day?.['font-size'];
  const cellFontWeight = dp?.cell?.day?.['font-weight'];
  const cellLineHeight = dp?.cell?.day?.['line-height'];
  const weekHeaderFontSize = dp?.['week-header']?.['font-size'];
  const weekHeaderFontWeight = dp?.['week-header']?.['font-weight'];
  const weekHeaderLineHeight = dp?.['week-header']?.['line-height'];
  const fieldValueGap = input?.line?.['child-end-container']?.['padding-left'];

  return {
    field: {
      _: {
        display: 'flex',
        height: '36px',
        width: '300px',
        'align-items': 'center',
        outline: 'none',
        'background-color': bgSurface,
        'border-width': input?.box?.['border-width'],
        'border-style': input?.box?.['border-style'],
        'border-color': input?.box?.['border-color'],
        'border-radius': input?.['border-top-left-radius'],
        cursor: 'pointer',
        transition: fieldTransition,
        '&:hover': {
          'box-shadow': buildBoxShadow(input, 'hover'),
        },
        '&:focus': {
          'box-shadow': buildBoxShadow(input, 'focus'),
          'border-color': input?.['border-bottom-color']?.focus,
        },
        '&:active': {
          'box-shadow': buildBoxShadow(input, 'pressed'),
        },
        '& svg': {
          color: iconMedium,
        },
      },
      error: {
        true: {
          'border-color': input?.error?.['border-bottom-color'],
        },
        false: {},
      },
      disabled: {
        true: {
          cursor: 'default',
          'background-color': generic?.color?.charcoal?.[100]?.[100],
          'border-color': generic?.color?.charcoal?.[200]?.[100],
          '&:hover': {
            'box-shadow': 'none',
          },
          '&:focus': {
            'box-shadow': 'none',
          },
          '&:active': {
            'box-shadow': 'none',
          },
          '& svg': {
            color: semantic?.['icon-color']?.input?.selected?.default,
          },
        },
        false: {},
      },
    },
    fieldValue: {
      _: {
        display: 'flex',
        'align-items': 'center',
        outline: 'none',
        'border-radius': borderRadius4,
        'font-family': fontFamily,
        'font-size': input?.['font-size'],
        'line-height': input?.['line-height'],
        'font-weight': input?.['font-weight'],
        'margin-left': fieldValueGap,
        'margin-right': fieldValueGap,
      },
      selecting: {
        true: {
          'background-color': input?.['background-color']?.selected,
        },
        false: {
          'background-color': 'transparent',
        },
      },
      hasValue: {
        true: {
          color: textPrimary,
        },
        false: {
          color: textSubtle,
        },
      },
    },
    cell: {
      _: {
        'font-family': fontFamily,
        'font-size': cellFontSize,
        'font-weight': cellFontWeight,
        'line-height': cellLineHeight,
        'box-sizing': 'border-box',
      },
      variant: {
        default: {
          'border-radius': '0',
          color: textPrimary,
          'background-color': bgSurface,
          '&:hover': {
            'background-color': dp?.cell?.['background-color']?.hover,
          },
          '&:active': {
            'background-color': dp?.cell?.['background-color']?.pressed,
          },
        },
        rangeMiddle: getWeaveRangeMiddleCell(dp, textPrimary),
        rangeStart: getWeaveRangeEndpointCell(dp),
        rangeEnd: getWeaveRangeEndpointCell(dp),
        outsideRangeMiddle: getWeaveRangeMiddleCell(dp, textPrimary),
        outsideRangeStart: getWeaveRangeEndpointCell(dp),
        outsideRangeEnd: getWeaveRangeEndpointCell(dp),
        outside: {
          'border-radius': '0',
          color: textSubtle,
          opacity: '0.4',
          'background-color': bgSurface,
          '&:hover': {
            'background-color': dp?.cell?.['background-color']?.hover,
          },
          '&:focus:not(:active)': {
            outline: 'none',
            'box-shadow': focusBoxShadow,
          },
          '&:active': {
            'background-color': dp?.cell?.['background-color']?.pressed,
          },
        },
        spacer: {
          cursor: 'default',
          'pointer-events': 'none',
          'border-radius': '0',
          'background-color': 'transparent',
          'font-family': fontFamily,
          'font-size': weekHeaderFontSize,
          'font-weight': weekHeaderFontWeight,
          'line-height': weekHeaderLineHeight,
          color: dp?.['week-header']?.['text-color'],
        },
      },
      today: {
        true: {
          color: dp?.cell?.['text-color']?.notable,
        },
        false: {},
      },
      disabled: {
        true: {
          color: textSubtle,
          opacity: '0.4',
          'background-color': bgSurface,
          cursor: 'not-allowed',
          '&:hover': {
            'box-shadow': 'none',
            'background-color': bgSurface,
          },
          '&:focus, &:active': {
            'background-color': bgSurface,
            'box-shadow': 'none',
          },
        },
        false: {},
      },
      selected: {
        true: {
          'border-radius': '0',
          color: dp?.cell?.['text-color']?.selected,
          'background-color': dp?.cell?.['background-color']?.selected,
          '&:hover': {
            'background-color': dp?.cell?.['background-color']?.selected,
          },
        },
        false: {},
      },
      compoundVariants: [
        {
          selected: 'false',
          today: 'false',
          styles: {
            '&:focus:not(:active)': {
              outline: 'none',
              'box-shadow': focusBoxShadow,
            },
          },
        },
        {
          selected: 'false',
          today: 'true',
          styles: {
            'box-shadow': todayInsetRing,
            '&:focus:not(:active)': {
              outline: 'none',
              'box-shadow': joinBoxShadows(todayInsetRing, focusBoxShadow),
            },
          },
        },
        {
          selected: 'true',
          styles: {
            '&:focus:not(:active)': {
              outline: 'none',
              'box-shadow': selectedFocusInset,
            },
          },
        },
      ],
    },
    eventDot: {
      _: {
        position: 'absolute',
        top: '30px',
        'border-radius': '50%',
        'min-height': '6px',
        'min-width': '6px',
        height: '6px',
        width: '6px',
        'max-height': '6px',
        'max-width': '6px',
      },
      selected: {
        true: {
          'background-color': dp?.cell?.['text-color']?.selected,
        },
        false: {
          'background-color': dp?.cell?.['text-color']?.notable ?? textPrimary,
        },
      },
    },
    calendar: {
      _: {
        display: 'flex',
        'flex-direction': 'column',
        'padding-left': dp?.header?.['padding-left'],
        'padding-right': dp?.header?.['padding-right'],
        'padding-bottom': dp?.['calendar-container']?.['padding-bottom'],
        'padding-top': '0',
        'column-gap': dp?.range?.calendar?.['column-gap'],
      },
      header: {
        display: 'flex',
        'justify-content': 'space-between',
        'align-items': 'center',
        'padding-bottom': dp?.header?.['padding-bottom'],
        position: 'relative',
      },
      navButton: {
        position: 'static',
        color: iconMedium,
        '&:hover, &:focus, &:focus-visible, &:active': {
          color: iconMedium,
        },
        '& svg, & svg path': {
          color: iconMedium,
          fill: iconMedium,
        },
        '&:hover svg, &:hover svg path, &:focus svg, &:focus svg path, &:focus-visible svg, &:focus-visible svg path, &:active svg, &:active svg path':
          {
            color: iconMedium,
            fill: iconMedium,
          },
        '&:disabled': {
          color: textSubtle,
          '& svg, & svg path': {
            color: textSubtle,
            fill: textSubtle,
          },
        },
      },
      weekHeader: {
        display: 'flex',
        'justify-content': 'flex-start',
        'padding-bottom': dp?.['week-header']?.['padding-bottom'],
        'font-family': fontFamily,
        'font-size': weekHeaderFontSize,
        'font-weight': weekHeaderFontWeight,
        'line-height': weekHeaderLineHeight,
        color: dp?.['week-header']?.['text-color'],
      },
      weeks: {
        display: 'flex',
        'flex-direction': 'column',
        'background-color': bgSurface,
        'align-self': 'flex-start',
        width: '100%',
        'border-top-width': '1px',
        'border-top-style': 'solid',
        'border-top-color': cellSeparatorColor,
        'border-left-width': '1px',
        'border-left-style': 'solid',
        'border-left-color': cellSeparatorColor,
      },
      weekRow: {
        display: 'flex',
        margin: '0',
      },
      cellBorder: {
        'border-right-width': '1px',
        'border-right-style': 'solid',
        'border-right-color': cellSeparatorColor,
        'border-bottom-width': '1px',
        'border-bottom-style': 'solid',
        'border-bottom-color': cellSeparatorColor,
        'border-radius': '0',
      },
      cellWrapper: {
        margin: '0',
      },
      grid: {},
    },
    overlay: {
      _: {
        display: 'flex',
        'flex-direction': 'column',
        'background-color': bgSurface,
        'box-shadow': semantic?.shadow?.high,
        'border-radius': borderRadius4,
        'padding-top': dp?.header?.['padding-top'],
        'padding-bottom': '0',
      },
    },
    yearMonthDropdown: {
      _: {
        color: dp?.header?.['text-color'],
        border: 'none',
        width: 'auto',
        'font-family': fontFamily,
        'font-size': dp?.header?.['font-size'],
        'font-weight': dp?.header?.['font-weight'],
        'line-height': dp?.header?.['line-height'],
        'padding-left': generic?.spacing?.[8],
        'padding-right': generic?.spacing?.[8],
        'margin-left': generic?.spacing?.[16],
      },
      disabled: {
        true: {
          color: textSubtle,
        },
        false: {},
      },
      icon: {
        'margin-left': generic?.spacing?.[8],
        width: dp?.header?.['font-size'],
        height: dp?.header?.['font-size'],
        color: iconMedium,
        fill: iconMedium,
      },
    },
    toggleIcon: {
      _: {
        color: iconMedium,
      },
      open: {
        true: {
          transform: 'rotate(180deg)',
        },
        false: {},
      },
    },
    footer: {
      _: {
        'padding-top': dp?.footer?.['padding-top'],
        'padding-bottom': dp?.footer?.['padding-top'],
        'padding-left': dp?.footer?.['padding-left'],
        'padding-right': dp?.footer?.['padding-right'],
        'border-top-width': '0',
        'border-top-style': 'solid',
        'border-top-color': 'transparent',
      },
      actions: {
        'min-height': weaveButton?.height,
      },
    },
    arrow: {
      'background-color': bgSurface,
    },
    cellWidth,
    cellHeight,
    calendarWidth,
    doubleCalendarWidth,
  };
};
